import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Eye, FileText, CreditCard, Plus, Check, Download } from 'lucide-react';
import { invoicesApi, customersApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

export default function InvoiceList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'unbilled' | 'all'>('unbilled');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedSettlements, setSelectedSettlements] = useState<string[]>([]);
  const [createForm, setCreateForm] = useState({
    customerId: '',
    invoiceYearMonth: new Date().toISOString().slice(0, 7),
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
  });
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    paymentAmount: '',
    remarks: '',
  });

  // メイン日付フィルタ
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['invoices', tab, search, dateFrom, dateTo],
    queryFn: () => tab === 'unbilled' 
      ? invoicesApi.getUnbilled({ })
      : invoicesApi.getAll({ search, dateFrom, dateTo }),
    enabled: tab === 'unbilled' || Boolean(dateFrom && dateTo),
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customersApi.getAll({ active: 'true' }),
  });

  const createMutation = useMutation({
    mutationFn: invoicesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('請求を作成しました');
      setIsCreateModalOpen(false);
      setSelectedSettlements([]);
      resetCreateForm();
    },
    onError: () => toast.error('請求作成に失敗しました'),
  });

  const paymentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => invoicesApi.payment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('入金を登録しました');
      setIsPaymentModalOpen(false);
      setSelectedItem(null);
    },
    onError: () => toast.error('入金登録に失敗しました'),
  });

  const resetCreateForm = () => {
    setCreateForm({
      customerId: '',
      invoiceYearMonth: new Date().toISOString().slice(0, 7),
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
    });
  };

  const handleToggleSettlement = (settlementId: string) => {
    setSelectedSettlements(prev => 
      prev.includes(settlementId) 
        ? prev.filter(id => id !== settlementId)
        : [...prev, settlementId]
    );
  };

  const handleOpenCreateModal = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('請求対象の精算がありません');
      return;
    }
    setIsCreateModalOpen(true);
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSettlements.length === 0) {
      toast.error('請求対象の精算を選択してください');
      return;
    }

    createMutation.mutate({
      customerId: createForm.customerId,
      invoiceYearMonth: createForm.invoiceYearMonth,
      invoiceDate: createForm.invoiceDate,
      dueDate: createForm.dueDate,
      settlementIds: selectedSettlements,
    });
  };

  const handlePayment = (row: any) => {
    setSelectedItem(row);
    setPaymentForm({
      paymentDate: new Date().toISOString().split('T')[0],
      paymentAmount: row.invoice?.totalAmount?.toString() || '',
      remarks: '',
    });
    setIsPaymentModalOpen(true);
  };

  const handleSubmitPayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    paymentMutation.mutate({
      id: selectedItem.invoice.id,
      data: {
        paymentDate: paymentForm.paymentDate,
        paymentAmount: paymentForm.paymentAmount,
        remarks: paymentForm.remarks,
      },
    });
  };

  const handleDetail = (row: any) => {
    setSelectedItem(row);
    setIsDetailModalOpen(true);
  };

  const handleExportUnbilled = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }

    const headers = ['LOT', '日付', '売り先', '商品名', '確定重量(kg)', '売単価', '金額'];
    const rows = items.map((item: any) => {
      const weight = Number(item.settlement?.confirmedWeight || item.settlement?.estimatedWeight) || 0;
      const price = Number(item.settlement?.sellingUnitPrice) || 0;
      return [
        item.settlement?.settlementLot || '',
        item.settlement?.processingDate || '',
        item.customer?.name || '',
        item.purchase?.productName || '',
        weight,
        price,
        weight * price,
      ];
    });

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `未請求一覧_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const handleExportAll = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }

    const headers = ['請求年月', '売り先', '請求金額', '状態', '請求日', '入金日'];
    const rows = items.map((item: any) => [
      item.invoice?.invoiceYearMonth || '',
      item.customer?.name || '',
      item.invoice?.totalAmount || '',
      item.invoice?.status === 'paid' ? '入金済' :
      item.invoice?.status === 'billed' ? '請求済' : '未請求',
      item.invoice?.invoiceDate || '',
      item.invoice?.paymentDate || '',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `請求一覧_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const formatNumber = (num: any) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('ja-JP');
  };

  const unbilledColumns = [
    {
      key: 'select',
      label: '',
      width: '40px',
      render: (row: any) => (
        <input
          type="checkbox"
          checked={selectedSettlements.includes(row.settlement?.id)}
          onChange={() => handleToggleSettlement(row.settlement?.id)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    { 
      key: 'settlement.settlementLot', 
      label: 'LOT', 
      width: '100px',
      render: (row: any) => row.settlement?.settlementLot || '-',
    },
    { 
      key: 'settlement.processingDate', 
      label: '日付', 
      width: '100px',
      render: (row: any) => row.settlement?.processingDate || '-',
    },
    { 
      key: 'customer.name', 
      label: '売り先',
      render: (row: any) => row.customer?.name || '-',
    },
    { 
      key: 'purchase.productName', 
      label: '商品名',
      render: (row: any) => row.purchase?.productName || '-',
    },
    { 
      key: 'settlement.confirmedWeight', 
      label: '確定重量(kg)',
      width: '110px',
      render: (row: any) => formatNumber(row.settlement?.confirmedWeight || row.settlement?.estimatedWeight),
    },
    { 
      key: 'settlement.sellingUnitPrice', 
      label: '売単価',
      width: '100px',
      render: (row: any) => `¥${formatNumber(row.settlement?.sellingUnitPrice)}`,
    },
    { 
      key: 'amount', 
      label: '金額',
      width: '120px',
      render: (row: any) => {
        const weight = Number(row.settlement?.confirmedWeight || row.settlement?.estimatedWeight) || 0;
        const price = Number(row.settlement?.sellingUnitPrice) || 0;
        return `¥${formatNumber(weight * price)}`;
      },
    },
  ];

  const invoiceColumns = [
    { 
      key: 'invoice.invoiceYearMonth', 
      label: '請求年月', 
      width: '100px',
      render: (row: any) => row.invoice?.invoiceYearMonth || '-',
    },
    { 
      key: 'customer.name', 
      label: '売り先',
      render: (row: any) => row.customer?.name || '-',
    },
    { 
      key: 'invoice.totalAmount', 
      label: '請求金額',
      width: '140px',
      render: (row: any) => `¥${formatNumber(row.invoice?.totalAmount)}`,
    },
    {
      key: 'invoice.status',
      label: '状態',
      width: '100px',
      render: (row: any) => {
        const status = row.invoice?.status;
        return (
          <span className={
            status === 'paid' ? 'badge badge-success' :
            status === 'billed' ? 'badge badge-warning' :
            'badge badge-error'
          }>
            {status === 'paid' ? '入金済' :
             status === 'billed' ? '請求済' : '未請求'}
          </span>
        );
      },
    },
    { 
      key: 'invoice.invoiceDate', 
      label: '請求日',
      width: '100px',
      render: (row: any) => row.invoice?.invoiceDate || '-',
    },
    { 
      key: 'invoice.paymentDate', 
      label: '入金日',
      width: '100px',
      render: (row: any) => row.invoice?.paymentDate || '-',
    },
    {
      key: 'actions',
      label: '操作',
      width: '120px',
      render: (row: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleDetail(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="詳細"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toast('請求書PDFを生成します'); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--text-secondary)' }}
            title="請求書"
          >
            <FileText size={16} />
          </button>
          {row.invoice?.status === 'billed' && (
            <button
              onClick={(e) => { e.stopPropagation(); handlePayment(row); }}
              className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
              style={{ color: 'var(--success-color)' }}
              title="入金登録"
            >
              <CreditCard size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Filter unbilled by customer when selected
  const filteredUnbilledData = createForm.customerId 
    ? (data?.data?.data || []).filter((item: any) => item.customer?.id === createForm.customerId)
    : (data?.data?.data || []);

  // Calculate selected total
  const selectedTotal = selectedSettlements.reduce((total, id) => {
    const item = (data?.data?.data || []).find((i: any) => i.settlement?.id === id);
    if (item) {
      const weight = Number(item.settlement?.confirmedWeight || item.settlement?.estimatedWeight) || 0;
      const price = Number(item.settlement?.sellingUnitPrice) || 0;
      return total + (weight * price);
    }
    return total;
  }, 0);

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="請求管理"
        breadcrumbs={[{ label: '請求管理' }]}
        dateFilterKey="invoices-date-filter"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="売り先で検索..."
        onApply={() => refetch()}
        actions={
          <button className="btn btn-outline btn-sm" onClick={tab === 'unbilled' ? handleExportUnbilled : handleExportAll}>
            <Download size={14} />
          </button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            tab === 'unbilled' 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setTab('unbilled')}
        >
          未請求一覧
        </button>
        <button
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            tab === 'all' 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setTab('all')}
        >
          請求一覧
        </button>
      </div>

      {/* Selection summary for unbilled */}
      {tab === 'unbilled' && selectedSettlements.length > 0 && (
        <div className="card mb-4 flex items-center justify-between">
          <div>
            <span style={{ color: 'var(--text-secondary)' }}>選択中: </span>
            <span className="font-bold" style={{ color: 'var(--text-primary)' }}>
              {selectedSettlements.length}件
            </span>
            <span className="mx-4" style={{ color: 'var(--text-secondary)' }}>|</span>
            <span style={{ color: 'var(--text-secondary)' }}>合計: </span>
            <span className="font-bold text-lg" style={{ color: 'var(--success-color)' }}>
              ¥{formatNumber(selectedTotal)}
            </span>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleOpenCreateModal}
          >
            <Plus size={16} />
            請求作成
          </button>
        </div>
      )}

      <DataTable
        columns={tab === 'unbilled' ? unbilledColumns : invoiceColumns}
        data={tab === 'unbilled' ? filteredUnbilledData : (data?.data?.data || [])}
        loading={isLoading}
        emptyMessage={tab === 'unbilled' ? '未請求の精算がありません' : '請求データがありません'}
        defaultSortKey={tab === 'unbilled' ? 'customer.name' : 'invoice.invoiceDate'}
        defaultSortOrder="desc"
      />

      {/* Create Invoice Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => { setIsCreateModalOpen(false); setSelectedSettlements([]); resetCreateForm(); }}
        title="請求作成"
        size="md"
      >
        <form onSubmit={handleSubmitCreate} className="space-y-4">
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
              選択した精算: {selectedSettlements.length}件
            </p>
            <p className="text-xl font-bold mt-1" style={{ color: 'var(--success-color)' }}>
              請求合計: ¥{formatNumber(selectedTotal)}
            </p>
          </div>

          <div>
            <label className="label">売り先 <span className="text-red-500">*</span></label>
            <select
              className="input-field"
              value={createForm.customerId}
              onChange={(e) => setCreateForm({ ...createForm, customerId: e.target.value })}
              required
            >
              <option value="">選択してください</option>
              {customersData?.data?.data?.map((customer: any) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">請求年月 <span className="text-red-500">*</span></label>
              <input
                type="month"
                className="input-field"
                value={createForm.invoiceYearMonth}
                onChange={(e) => setCreateForm({ ...createForm, invoiceYearMonth: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label">請求日 <span className="text-red-500">*</span></label>
              <input
                type="date"
                className="input-field"
                value={createForm.invoiceDate}
                onChange={(e) => setCreateForm({ ...createForm, invoiceDate: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="label">支払期限</label>
            <input
              type="date"
              className="input-field"
              value={createForm.dueDate}
              onChange={(e) => setCreateForm({ ...createForm, dueDate: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setIsCreateModalOpen(false); setSelectedSettlements([]); resetCreateForm(); }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending}
            >
              <Check size={16} />
              請求作成
            </button>
          </div>
        </form>
      </Modal>

      {/* Payment Modal */}
      <Modal
        isOpen={isPaymentModalOpen}
        onClose={() => { setIsPaymentModalOpen(false); setSelectedItem(null); }}
        title="入金登録"
        size="md"
      >
        {selectedItem && (
          <form onSubmit={handleSubmitPayment} className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {selectedItem.customer?.name}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                請求年月: {selectedItem.invoice?.invoiceYearMonth} |
                請求日: {selectedItem.invoice?.invoiceDate}
              </p>
              <p className="text-xl font-bold mt-2" style={{ color: 'var(--text-primary)' }}>
                請求金額: ¥{formatNumber(selectedItem.invoice?.totalAmount)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">入金日 <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  className="input-field"
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">入金額 <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  className="input-field"
                  value={paymentForm.paymentAmount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentAmount: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="label">備考</label>
              <textarea
                className="input-field"
                rows={2}
                value={paymentForm.remarks}
                onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setIsPaymentModalOpen(false); setSelectedItem(null); }}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={paymentMutation.isPending}
              >
                <CreditCard size={16} />
                入金登録
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => { setIsDetailModalOpen(false); setSelectedItem(null); }}
        title="請求詳細"
        size="lg"
      >
        {selectedItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>売り先</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.customer?.name}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>請求年月</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.invoice?.invoiceYearMonth}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>請求日</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.invoice?.invoiceDate}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>支払期限</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedItem.invoice?.dueDate || '-'}
                </p>
              </div>
            </div>

            <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>請求金額</p>
                  <p className="font-bold text-xl" style={{ color: 'var(--text-primary)' }}>
                    ¥{formatNumber(selectedItem.invoice?.totalAmount)}
                  </p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>状態</p>
                  <p className="font-bold text-xl" style={{ 
                    color: selectedItem.invoice?.status === 'paid' ? 'var(--success-color)' : 'var(--warning-color)'
                  }}>
                    {selectedItem.invoice?.status === 'paid' ? '入金済' :
                     selectedItem.invoice?.status === 'billed' ? '請求済' : '未請求'}
                  </p>
                </div>
                {selectedItem.invoice?.paymentDate && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>入金日</p>
                    <p className="font-bold text-xl" style={{ color: 'var(--text-primary)' }}>
                      {selectedItem.invoice?.paymentDate}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                className="btn btn-outline"
                onClick={() => { setIsDetailModalOpen(false); setSelectedItem(null); }}
              >
                閉じる
              </button>
              {selectedItem.invoice?.status === 'billed' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    handlePayment(selectedItem);
                  }}
                >
                  <CreditCard size={16} />
                  入金登録
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
